
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

import type { TTStringFormat } from '@/TixTime/helpers'
import { API } from '@/api/API'
import type { MembershipRules } from '@/api/Membership'
import type { EventWithConfig, LinkedTG } from '@/api/types/processedEntities'
import { apiErrorMessageOrRethrow } from '@/errors/helpers'
import { getMembershipConfigProperty } from '@/helpers/Config'
import { environment } from '@/helpers/Environment'
import { indexBy, indexItemsById } from '@/helpers/IndexHelpers'
import type { LanguageStrings } from '@/language/types'
import { type MembershipWithEntity, membershipIsRenewable, membershipRenewableFrom } from '@/state/Membership'
import store from '@/store/store'

interface WalletConfig {
  apple: boolean
  google: boolean
}

@Component({ name: 'PurchasedMembership' })
export default class MyMembership extends Vue {
  @Prop({ required: true })
  membership: MembershipWithEntity

  @Prop({ required: true })
  tickets: Ticket[]

  @Prop()
  savedCard: SavedPaymentMethod | null

  @Prop()
  membershipRules: MembershipRules | null

  resendingMembershipEmail: boolean = false
  showEmailSentMessage: boolean = false

  loadingWallets: boolean = false

  // Disabled state in case loading fails.
  walletConfig: WalletConfig = { apple: false, google: false }

  // Options plugin properties.
  t: LanguageStrings['purchasedMembership']

  @Watch('membershipRules', { immediate: true })
  onMembershipRulesChange() {
    // Do not show nor check the add-to-wallet buttons if there is no primary ticket, such as wrong seller.
    if (this.tickets && this.membershipRules) {
      this.loadingWallets = true
      this.initWalletConfig().finally(() => {
        this.loadingWallets = false
      })
    }
  }

  /**
   * There are several ambiguities regarding when to display the "Add to Apple/Google Wallet" buttons.
   * For now, we are choosing the simplest yet acceptable approach:
   * show the buttons if their corresponding wallets are configured even if the device doesn't support it.
   *
   * Alternatives we considered:
   *
   * 1. Detect the OS and browser using `navigator.userAgent`.
   *    This approach was rejected because relying on user agent strings is generally discouraged:
   *    they are inconsistent, easily spoofed, and prone to breaking with browser updates.
   *
   * 2. Always display the "Add to Google Wallet" button when Google Wallet is configured,
   *    and show the "Add to Apple Wallet" button only if `window.ApplePaySession` is present.
   *    While this is a reasonable proxy for Apple Wallet support, there are edge cases (e.g., iPhone 5s and earlier)
   *    where Apple Wallet is available but Apple Pay is not.
   *
   * @see https://github.com/stqry/tix-web-client/pull/2850#pullrequestreview-2731053870
   */
  async initWalletConfig(): Promise<WalletConfig | void> {
    try {
      const response = await API.getUncached<'wallet_precheck'>(`ticket/${this.primaryTicketId}/walletprecheck`)
      const { apple, google } = indexBy('provider', response.wallet_precheck._data)
      this.walletConfig = {
        apple: apple.ok!,
        google: google.ok!,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- apiErrorMessageOrRethrow requires any for complex error type compatibility
    } catch (e: any) {
      // A 404 error is expected if no wallets are configured,
      // or if the user is viewing their membership under the wrong seller.
      apiErrorMessageOrRethrow(e, ['not_found'])
    }
  }

  get primaryTicketId(): string {
    const types: Dict<TicketType> = indexItemsById(store.getters['Member/ticketTypes'])

    // Attach ticket type rank and sort by it.
    const sorted = this.primaryTickets
      .map((ticket) => ({ id: ticket.id, rank: types[ticket.ticket_type_id]._rank }))
      .sort((a, b) => a.rank - b.rank)

    // Tickets may be empty, such as when viewing a membership from a different seller.
    return sorted[0]!.id
  }

  private get primaryTickets(): Ticket[] {
    // Prefer tickets for the primary member.
    const primary = this.tickets.filter((ticket) => this.memberTTIDs.has(ticket.ticket_type_id))
    return primary.length > 0 ? primary : this.tickets
  }

  private get memberTTIDs(): Set<string> {
    const result = new Set<string>()

    for (const rule of this.membershipRules!.rules) {
      if (rule.party_role === 'Member') {
        result.add(rule.ticket_type_id)
      }
    }

    return result
  }

  get cardIcon() {
    if (!this.savedCard) {
      return null
    }

    const identifier = this.savedCard.identifier
    const brand = identifier.toLowerCase().split('…')[0].replace(/\s/g, '')
    return `/assets/default/images/${brand}.svg`
  }

  resendMembershipEmail() {
    this.showEmailSentMessage = false
    this.resendingMembershipEmail = true
    API.post('my/ticket_order/resend', { body: { memberships_only: true } })
      .then(() => {
        this.showEmailSentMessage = true
      })
      .finally(() => {
        this.resendingMembershipEmail = false
      })
  }

  get isRenewable(): boolean {
    // TODO Update membershipIsRenewable to accept just MembershipWithEntity.
    return membershipIsRenewable(this.membership, this.group, this.event)
  }

  get isExpired(): boolean {
    return this.membership.validTo.isBefore()
  }

  get expiry(): string {
    const medium = this.$t('dateFormat.medium') as TTStringFormat
    const formattedExpiry = this.membership.validTo.format(medium)
    // TODO Update membershipRenewableFrom to accept just MembershipWithEntity.
    const renewableFrom = membershipRenewableFrom(this.membership, this.group, this.event)
    return this.isRenewable || this.isExpired
      ? formattedExpiry
      : `${formattedExpiry} (renew from ${renewableFrom.format(medium)})`
  }

  get autoRenewEnabled(): boolean {
    const legacy = environment.config.ask_to_autorenew ? 'prompt' : undefined
    const config = getMembershipConfigProperty('renewal', 'automatic', this.group, this.event, legacy)
    return config === 'prompt'
  }

  get group(): LinkedTG {
    return this.membership.ticketGroup
  }

  get event(): EventWithConfig {
    return this.membership.eventTemplate
  }

  get cancelAutoRenewLink(): string {
    return `/manage/${this.membership.entity.ticket_order_id}/cancel-auto-renew`
  }
}
